import React, { useEffect, useState } from "react";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PROJECTS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, FolderKanban, Trash2, MapPin, Edit2, Shield, ShieldCheck, User as UserIcon, X, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function MemberChip({ user, kind, onRemove }) {
  const Icon = kind === "admin" ? Shield : UserIcon;
  return (
    <div className="inline-flex items-center gap-2 px-2 py-1 rounded-md border border-border bg-secondary/40 text-xs">
      <Icon className="w-3 h-3 text-muted-foreground" />
      <span className="font-medium">{user.name}</span>
      <span className="text-muted-foreground">{user.email}</span>
      <button onClick={onRemove} className="ml-1 hover:text-destructive" title="Hapus dari proyek">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

export default function Projects() {
  const { projects, fetchProjects, selectProject } = useProject();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", location: "" });
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    location: "",
    daily_water_limit_l: 1000,
    battery_capacity_wh: 1000,
    battery_voltage_min: 11.4,
    battery_voltage_max: 13.5,
  });
  // Invite dialog state — one shared dialog reused per project card
  const [inviteTarget, setInviteTarget] = useState(null); // project object
  const [inviteForm, setInviteForm] = useState({ email: "", as_admin: false });

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const usersById = Object.fromEntries(users.map((u) => [u.id, u]));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/projects", form);
      toast.success("Proyek dibuat");
      setOpen(false);
      setForm({ name: "", description: "", location: "" });
      await fetchProjects();
      selectProject(data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat proyek");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus proyek beserta semua data sensornya?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Proyek dihapus");
    fetchProjects();
  };

  const openEdit = (p) => {
    setEditTarget(p);
    setEditForm({
      name: p.name,
      description: p.description,
      location: p.location,
      daily_water_limit_l: p.daily_water_limit_l,
      battery_capacity_wh: p.battery_capacity_wh ?? 1000,
      battery_voltage_min: p.battery_voltage_min ?? 11.4,
      battery_voltage_max: p.battery_voltage_max ?? 13.5,
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/projects/${editTarget.id}`, {
        name: editForm.name,
        description: editForm.description,
        location: editForm.location,
        daily_water_limit_l: Number(editForm.daily_water_limit_l),
        battery_capacity_wh: Number(editForm.battery_capacity_wh),
        battery_voltage_min: Number(editForm.battery_voltage_min),
        battery_voltage_max: Number(editForm.battery_voltage_max),
      });
      toast.success("Proyek diperbarui");
      setEditTarget(null);
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    }
  };

  const removeMember = async (projectId, userId) => {
    if (!window.confirm("Hapus anggota dari proyek ini?")) return;
    try {
      await api.delete(`/projects/${projectId}/members/${userId}`);
      toast.success("Anggota dihapus");
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    }
  };

  const openInvite = (project) => {
    setInviteTarget(project);
    setInviteForm({ email: "", as_admin: false });
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    if (!inviteTarget || !inviteForm.email) return;
    try {
      await api.post(`/projects/${inviteTarget.id}/invite`, {
        email: inviteForm.email,
        as_admin: inviteForm.as_admin,
      });
      toast.success(
        inviteForm.as_admin
          ? "Admin Anggota berhasil ditambahkan"
          : "User berhasil ditambahkan ke proyek"
      );
      setInviteTarget(null);
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menambahkan anggota");
    }
  };

  return (
    <div>
      <PageHeader
        title="Manajemen Proyek"
        overline="Master Admin"
        description="Buat dan kelola proyek irigasi. Tambah / hapus Admin Anggota & User langsung dari kartu proyek."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid={PROJECTS.newButton}>
                <Plus className="w-4 h-4 mr-1" /> Proyek Baru
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Proyek Baru</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nama proyek</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid={PROJECTS.nameInput} />
                </div>
                <div className="space-y-2">
                  <Label>Deskripsi</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid={PROJECTS.descInput} />
                </div>
                <div className="space-y-2">
                  <Label>Lokasi</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid={PROJECTS.locationInput} />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid={PROJECTS.submitButton}>Buat</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {projects.map((p) => {
          const admins = (p.admin_member_ids || []).map((id) => usersById[id]).filter(Boolean);
          const regulars = (p.member_ids || []).map((id) => usersById[id]).filter(Boolean);
          return (
            <div key={p.id} className="border border-border rounded-lg bg-card p-6 flex flex-col" data-testid={`project-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center">
                  <FolderKanban className="w-4 h-4 text-primary" />
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`project-edit-${p.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(p.id)} data-testid={`project-delete-${p.id}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold">{p.name}</h3>
              {p.location && (
                <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="w-3 h-3" /> {p.location}
                </div>
              )}
              <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{p.description || "—"}</p>

              <div className="mt-4 pt-4 border-t border-border space-y-3">
                <div className="flex items-center justify-between">
                  <div className="overline text-muted-foreground">Anggota Proyek</div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => openInvite(p)}
                    data-testid={`project-invite-${p.id}`}
                  >
                    <UserPlus className="w-3 h-3 mr-1" /> Tambah Anggota
                  </Button>
                </div>
                <div>
                  <div className="overline text-muted-foreground mb-2 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Admin Anggota ({admins.length})
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {admins.length === 0 && <span className="text-xs text-muted-foreground italic">Belum ada</span>}
                    {admins.map((u) => (
                      <MemberChip key={u.id} user={u} kind="admin" onRemove={() => removeMember(p.id, u.id)} />
                    ))}
                  </div>
                </div>
                <div>
                  <div className="overline text-muted-foreground mb-2 flex items-center gap-1">
                    <UserIcon className="w-3 h-3" /> User ({regulars.length})
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {regulars.length === 0 && <span className="text-xs text-muted-foreground italic">Belum ada</span>}
                    {regulars.map((u) => (
                      <MemberChip key={u.id} user={u} kind="user" onRemove={() => removeMember(p.id, u.id)} />
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span>Batas: {p.daily_water_limit_l} L/hari</span>
                <Badge variant="outline" className="text-[10px]">{p.simulate_sensors === false ? "ESP32 ONLY" : "SIM"}</Badge>
              </div>
              <Button variant="outline" className="mt-4" onClick={() => selectProject(p.id)} data-testid={`project-select-${p.id}`}>
                Pilih Proyek
              </Button>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Proyek</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <form onSubmit={submitEdit} className="space-y-3">
              <div className="space-y-2">
                <Label>Nama</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required data-testid="project-edit-name" />
              </div>
              <div className="space-y-2">
                <Label>Deskripsi</Label>
                <Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Lokasi</Label>
                <Input value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Batas air harian (L)</Label>
                <Input type="number" value={editForm.daily_water_limit_l} onChange={(e) => setEditForm({ ...editForm, daily_water_limit_l: e.target.value })} />
              </div>
              <div className="pt-2 border-t border-border">
                <div className="overline text-muted-foreground mb-2">Kalibrasi Baterai (VRLA)</div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Kapasitas (Wh)</Label>
                    <Input
                      type="number"
                      step="1"
                      value={editForm.battery_capacity_wh}
                      onChange={(e) => setEditForm({ ...editForm, battery_capacity_wh: e.target.value })}
                      data-testid="project-edit-battery-capacity"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">V min (0% SoC)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editForm.battery_voltage_min}
                      onChange={(e) => setEditForm({ ...editForm, battery_voltage_min: e.target.value })}
                      data-testid="project-edit-vmin"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">V max (100% SoC)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editForm.battery_voltage_max}
                      onChange={(e) => setEditForm({ ...editForm, battery_voltage_max: e.target.value })}
                      data-testid="project-edit-vmax"
                    />
                  </div>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  SoC dihitung: (V_ukur − V_min) / (V_max − V_min) × 100%. Untuk VRLA 12V standar: 11.4 V (habis) → 13.5 V (penuh).
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="project-edit-submit">Simpan</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Invite dialog — add a user or admin member to the selected project */}
      <Dialog open={!!inviteTarget} onOpenChange={(o) => !o && setInviteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tambah Anggota — {inviteTarget?.name}</DialogTitle>
          </DialogHeader>
          {inviteTarget && (() => {
            const alreadyIn = new Set([
              ...(inviteTarget.member_ids || []),
              ...(inviteTarget.admin_member_ids || []),
            ]);
            const available = users.filter((u) => !alreadyIn.has(u.id));
            const filteredByRole = inviteForm.as_admin
              ? available.filter((u) => u.role === "admin" || u.role === "project_admin")
              : available;
            return (
              <form onSubmit={submitInvite} className="space-y-4">
                <div className="space-y-2">
                  <Label>Peran di proyek ini</Label>
                  <Select
                    value={inviteForm.as_admin ? "admin" : "user"}
                    onValueChange={(v) => setInviteForm({ email: "", as_admin: v === "admin" })}
                  >
                    <SelectTrigger data-testid="project-invite-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User (Anggota biasa)</SelectItem>
                      <SelectItem value="admin">Admin Anggota (bisa edit)</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="text-xs text-muted-foreground">
                    {inviteForm.as_admin
                      ? "Admin Anggota dapat menambah/edit sensor, konfigurasi solenoid, dan upload firmware."
                      : "User biasa hanya dapat memantau dashboard dan mengoperasikan katup manual."}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Pilih pengguna</Label>
                  <Select
                    value={inviteForm.email}
                    onValueChange={(v) => setInviteForm({ ...inviteForm, email: v })}
                  >
                    <SelectTrigger data-testid="project-invite-user">
                      <SelectValue placeholder={filteredByRole.length === 0 ? "Tidak ada pengguna tersedia" : "Pilih pengguna"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[280px]">
                      {filteredByRole.length === 0 ? (
                        <div className="px-3 py-6 text-sm text-muted-foreground text-center">
                          {inviteForm.as_admin
                            ? "Semua user berperan Admin/Admin Anggota sudah menjadi anggota. Buat akun baru di menu Pengguna."
                            : "Semua user sudah menjadi anggota proyek ini. Buat akun baru di menu Pengguna."}
                        </div>
                      ) : (
                        filteredByRole.map((u) => (
                          <SelectItem key={u.id} value={u.email}>
                            <div className="flex flex-col">
                              <span className="font-medium">{u.name}</span>
                              <span className="text-xs text-muted-foreground">
                                {u.email} · {u.role}
                              </span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={!inviteForm.email}
                    data-testid="project-invite-submit"
                  >
                    Tambahkan
                  </Button>
                </DialogFooter>
              </form>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
